import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  Renderer2
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import DropDownOptions from '@approot/modals/trade-capture/schedule-reports/report-dropdown-options.json';
import { AlertsService, AuthService } from '@approot/services';
import { SubscriptionSink, Utilities, reformatDate } from '@approot/utilities';
import dayjs from 'dayjs';
import { HistoricalTradesMeta } from '../trades-grid.component';

type GridData = any[];

interface IDataLoaded {
  data: GridData;
  meta?: HistoricalTradesMeta;
}

@Component({
  selector: 'app-load-portfolio',
  templateUrl: './historical-trades.component.html',
  styleUrls: ['./historical-trades.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HistoricalTradesComponent implements OnDestroy {
  @Input() public modalOpened = true;
  @Output() public modalOpenedChange = new EventEmitter<boolean>();
  @Output() public dataLoaded = new EventEmitter<IDataLoaded>();
  public state: 'LOADING' = null;
  public form: FormGroup;
  public timezones: string[] = DropDownOptions.timezones;

  private subsink = new SubscriptionSink();

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private changeRef: ChangeDetectorRef,
    private authService: AuthService,
    private alertService: AlertsService
  ) {
    this.form = new FormGroup({
      date: new FormControl('', Validators.required),
      time: new FormControl('12:00:00', Validators.required),
      timezone: new FormControl(Utilities.currentTimezone, Validators.required)
    });

    this.subsink.sink = this.form.get('date').valueChanges.subscribe(v => {
      const ctrl = this.form.get('date');
      if (/^(\d\d)\/(\d\d)\/(\d\d\d\d)$/.exec(ctrl.value)) {
        const date = dayjs(`${RegExp.$3}-${RegExp.$2}-${RegExp.$1}`).format(
          'DD MMM YYYY'
        );
        this.setDate(date);
        this.changeRef.markForCheck();
      }
    });
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public modalChanged(event: boolean) {
    this.modalOpenedChange.emit(event);
  }

  public load() {
    if (!this.form.valid) {
      return;
    }

    let date = this.form.get('date').value as string;
    date = reformatDate(date, 'YYYY-MM-DD');

    // Time can be hh:mm:ss hh:mm hhmmss hhmm
    let time = this.form.get('time').value as string;
    if (
      /^(\d\d):(\d\d)(?:(?:[:])(\d\d))?$/.test(time) ||
      /^(\d\d)(\d\d)(\d\d)?$/.test(time)
    ) {
      time = `${RegExp.$1}:${RegExp.$2}:${RegExp.$3 || '00'}`;
    }

    const timezone = this.form.get('timezone').value;

    let datetime: string;
    try {
      datetime = `${dayjs(date).format('D MMM YYYY')} ${time}`;
    } catch {
      datetime = `${date} ${time}`;
    }

    this.state = 'LOADING';
    this.dataLoaded.emit({ data: [], meta: { datetime, timezone } });
    const task = 'getTrades';
    this.authService
      .fxPost<any>(
        { task, date, time, timezone },
        'get-user-info',
        `task=${task}`
      )
      .subscribe({
        next: response => {
          this.state = null;
          this.dataLoaded.emit({ data: response });
          this.modalOpenedChange.emit(false);
          this.changeRef.markForCheck();
        },
        error: _ => {
          this.state = null;
          this.alertService.addAlert({
            message: 'Failed to load historical trades',
            type: 'error'
          });
          this.changeRef.markForCheck();
        }
      });
  }

  private setDate(dateValue: string) {
    setTimeout(() => {
      // clarity design date has issues setting the value on creation
      let dateEl =
        this.elementRef.nativeElement.querySelector('.input-date input');
      if (dateEl) {
        this.renderer.setProperty(dateEl, 'value', dateValue);
      }
      this.form.get('date').updateValueAndValidity();
    });
  }
}
